import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager";
import DimensionsField from "../shared/formfields/dimensions-field";
import { IDividerBlockData } from "../../../types/content-block-data";
import DimensionsBox from "../shared/satori-ui/dimensions-box";
import { Link, Unlink, RulerDimensionLine, Paintbrush, PanelLeftDashed } from "lucide-react";
import { NOPERCENT_UNITS } from "../../../types/constants";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import SatoriColorPicker from "../shared/satori-ui/colorpicker";

/**
 * DividerOptions – Sidebar Editing Panel for Divider Blocks
 *
 * Provides a full-featured sidebar panel for configuring all style and layout properties
 * of a Divider content block in the Satori PageBuilder. Includes controls for width, height,
 * color, padding, margins, and alignment, with support for linked and individual side editing.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Divider content block to edit.
 *
 * @example
 * // Render options panel for a given Divider block
 * <DividerOptions blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Divider block's data from the MobX contentManager store.
 * - Renders grouped UI controls for all editable Divider fields (width, height, color, padding, margins, alignment).
 * - Uses `contentManager.setDividerConfig` for atomic and reactive state updates.
 * - Padding and margin editors support both linked (all sides) and individual settings with full px/rem/% unit selection.
 * - Alignment is handled via a flex parent, supporting left, center, and right placement.
 * - Designed to mirror the update flow and structure of `ButtonOptions` and `ColumnsOptions` panels for consistency.
 * - Wrapped in MobX's `observer()` for real-time UI reactivity.
 *
 * @see contentManager.setDividerConfig
 * @see DividerBlock.tsx – For how these properties affect rendering
 * @see ButtonOptions, ColumnsOptions – For parallel options panel patterns
 */
export default observer(function DividerOptions({ blockId }: { blockId: string }){
  // Look up the current block
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "divider") return null;
  const dividerData = block.blockData as IDividerBlockData;

  return (
    <section className="flex flex-col">
      <SatoriCollapsiblePanel
        icon={<RulerDimensionLine size={18} />}
        title={"Layout"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Width */}
          <DimensionsBox
            label="Width"
            value={dividerData.width?.value || "100"}
            unit={dividerData.width?.unit || "%"}
            onValueChange={(val) =>
              contentManager.setDividerConfig(blockId, {
                ...dividerData,
                width: { value: val, unit: dividerData.width?.unit || "%" },
              })
            }
            onUnitChange={(unit) =>
              contentManager.setDividerConfig(blockId, {
                ...dividerData,
                width: { value: dividerData.width?.value || "100", unit },
              })
            }
          />

          {/* Height */}
          <DimensionsBox
            label="Height"
            value={dividerData.height?.value || "1"}
            unit={dividerData.height?.unit || "px"}
            onValueChange={(val) =>
              contentManager.setDividerConfig(blockId, {
                ...dividerData,
                height: { value: val, unit: dividerData.height?.unit || "px" },
              })
            }
            onUnitChange={(unit) =>
              contentManager.setDividerConfig(blockId, {
                ...dividerData,
                height: { value: dividerData.height?.value || "1", unit },
              })
            }
            className="w-[140px]"
          />
        </div>
      </SatoriCollapsiblePanel>
        
      <SatoriCollapsiblePanel
        icon={<Paintbrush size={18} />}
        title={"Appearance"}
        defaultOpen={true}
      >
        {/* Divider Color */}
        <SatoriColorPicker
          label={"Color"}
          value={dividerData.color || "#CCCCCC"}
          onChange={val =>
            contentManager.setDividerConfig(blockId, {
              ...dividerData,
              color: val,
            })
          }
        />
      </SatoriCollapsiblePanel>

      <SatoriCollapsiblePanel
        icon={<PanelLeftDashed size={18} />}
        title={"Alignment"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Padding sub-secton */}
          <div className="flex items-center gap-2 w-full justify-between">
            <span className="text-base">Padding</span>
            <button
              onClick={() =>
                contentManager.setDividerConfig(blockId, {
                  padding: {
                    ...dividerData?.padding!,
                    linked: !dividerData?.padding?.linked,
                  },
                })
              }
              className="ml-1 p-1"
              title={
                dividerData?.padding?.linked ? "Unlink sides" : "Link all sides"
              }
            >
              {dividerData?.padding?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {dividerData?.padding?.linked ? (
            <div className="flex flex-col gap-3">
              <DimensionsBox
                value={dividerData?.padding?.left || ""}
                unit={dividerData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      top: val,
                      right: val,
                      bottom: val,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 w-full">
              <DimensionsBox
                label={"Top"}
                value={dividerData?.padding?.top || ""}
                unit={dividerData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      top: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
              <DimensionsBox
                label={"Right"}
                value={dividerData?.padding?.right || ""}
                unit={dividerData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      right: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
              <DimensionsBox
                label={"Bottom"}
                value={dividerData?.padding?.bottom || ""}
                unit={dividerData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      bottom: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
              <DimensionsBox
                label={"Left"}
                value={dividerData?.padding?.left || ""}
                unit={dividerData?.padding?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    padding: {
                      ...dividerData?.padding!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
            </div>
          )}

          {/* Margins sub-secton */}
          <div className="flex items-center gap-3 w-full justify-between">
            <span className="text-base">Divider Margins</span>
            <button
              onClick={() =>
                contentManager.setDividerConfig(blockId, {
                  margins: {
                    ...dividerData?.margins!,
                    linked: !dividerData?.margins?.linked,
                  },
                })
              }
              className="ml-1 p-1"
              title={
                dividerData?.margins?.linked ? "Unlink sides" : "Link all sides"
              }
            >
              {dividerData?.margins?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {dividerData?.margins?.linked ? (
            <div className="flex flex-col gap-3">
              <DimensionsBox
                value={dividerData?.margins?.left || ""}
                unit={dividerData?.margins?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      top: val,
                      right: val,
                      bottom: val,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3 w-full">
              <DimensionsBox
                label={"Top"}
                value={dividerData?.margins?.top || ""}
                unit={dividerData?.margins?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      top: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
              <DimensionsBox
                label={"Right"}
                value={dividerData?.margins?.right || ""}
                unit={dividerData?.margins?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      right: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
              <DimensionsBox
                label={"Bottom"}
                value={dividerData?.margins?.bottom || ""}
                unit={dividerData?.margins?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      bottom: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
              <DimensionsBox
                label={"Left"}
                value={dividerData?.margins?.left || ""}
                unit={dividerData?.margins?.unit || "px"}
                onValueChange={(val) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal) =>
                  contentManager.setDividerConfig(blockId, {
                    margins: {
                      ...dividerData?.margins!,
                      unit: unitVal || "px",
                    },
                  })
                }
              />
            </div>
          )}
        </div>
      </SatoriCollapsiblePanel>
    </section>
  );
});
